import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  selectHasSupportedPharmacistCapability,
  selectPersonaKey,
  selectTokenUserId,
} from "../store/auth-slice";
import {
  consultPharmacyContextActions,
  selectConsultPharmacyPair,
  selectPharmacistProfile,
} from "../store/consultPharmacyContext-slice";
import {
  fetchConsultPharmacyDetail,
  fetchPharmacistProfile,
} from "../store/consultPharmacyContext-actions";

// ITAM-2622 (3.4) — headless. Mounted next to the other global managers so the
// consultation pharmacy context is loaded for BOTH personas on fresh login and
// on session restore, independently of which route the pharmacist lands on.
//
// Refresh triggers (a) and (c) live here; (b) the post-mutation refetch is the
// profile page's, through the same shared thunk, and (d) is the page's 30 s
// status refetch.
const ConsultPharmacyContextLoader = () => {
  const dispatch = useDispatch();
  const hasCapability = useSelector(selectHasSupportedPharmacistCapability);
  const consultantId = useSelector(selectTokenUserId);
  const personaKey = useSelector(selectPersonaKey);
  const profile = useSelector(selectPharmacistProfile);
  const pair = useSelector(selectConsultPharmacyPair);
  const status = useSelector(
    (state: RootState) => state.consultPharmacyContext.status,
  );
  const detailStatus = useSelector(
    (state: RootState) => state.consultPharmacyContext.detailStatus,
  );

  const armed = hasCapability && consultantId != null;

  // (a) initial load — covers fresh login, session restore, AND the
  // persona-changing token refresh. The latter is why `personaKey` is a
  // dependency: `auth/personaExit` resets the context slice while `armed` and
  // `consultantId` both survive a consult-only <-> dual-role transition, so
  // without the key this effect would never re-run and the wiped context —
  // and with it consult discovery — would stay empty until a full reload.
  useEffect(() => {
    if (!armed || consultantId == null) {
      return;
    }
    const controller = new AbortController();
    dispatch<any>(fetchPharmacistProfile(consultantId, controller.signal));
    return () => controller.abort();
  }, [dispatch, armed, consultantId, personaKey]);

  // (c) window focus while the capability is armed — three distinct demands
  // behind one listener, and never a poll:
  //
  //   - an EXISTING profile still has no pair: the defensive missing-pair path
  //     of decision 17. Admin/ops finishes the association elsewhere, and the
  //     pharmacist should not have to reload the app to pick it up. The
  //     profile requirement is load-bearing: before a profile exists there is
  //     no association to pick up, and the refetch would flip the context to
  //     `loading` with no profile — which swaps the onboarding form for the
  //     loading placeholder and unmounts the create button under the very
  //     click that follows the focus.
  //   - the FIRST load failed outright (non-404): `error` is only reachable
  //     with nothing loaded (`fetchFailed` keeps `loaded` otherwise), and no
  //     other AUTOMATIC trigger can retry it — effect (a)'s deps are stable
  //     and the page's 30 s poll needs a profile. The profile page's manual
  //     "Pokušaj ponovo" does cover its own visitors, but a dual-role
  //     pharmacist parked on the order board never sees it, so without this
  //     demand one transient blip at login would silently close the discovery
  //     gate for the whole session. NEVER armed for `noProfile`: that state is
  //     a server-confirmed 404, and refetching there is the swallowed-click
  //     bug above.
  //   - the enrichment errored: the `retry-on-demand` half of the 3.4 contract.
  //     Enrichment is fetched once per pair, so a single transient failure would
  //     otherwise last the whole session. Focus is a demand, not a timer, so
  //     this cannot become a failure loop: a retry that fails again just leaves
  //     `error` for the next focus.
  const needsPair =
    armed && consultantId != null && profile != null && pair == null;
  const loadFailed =
    armed && consultantId != null && profile == null && status === "error";
  const detailFailed = armed && detailStatus === "error";
  useEffect(() => {
    if (!needsPair && !loadFailed && !detailFailed) {
      return;
    }
    const onFocus = () => {
      if ((needsPair || loadFailed) && consultantId != null) {
        dispatch<any>(fetchPharmacistProfile(consultantId));
      }
      if (detailFailed) {
        dispatch(consultPharmacyContextActions.detailRetryRequested());
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, needsPair, loadFailed, detailFailed, consultantId]);

  // Enrichment, keyed by the pair. Never gates anything.
  useEffect(() => {
    if (!armed || pair == null || detailStatus !== "idle") {
      return;
    }
    dispatch<any>(
      fetchConsultPharmacyDetail(pair.marketplaceId, pair.branchMarketplaceId),
    );
  }, [dispatch, armed, pair, detailStatus]);

  return null;
};

export default ConsultPharmacyContextLoader;
